/**
 * Выбор объекта на телефоне — плитками.
 *
 * Плитка: номер объекта, сколько коммутаторов, полоска состояния (как на
 * дашборде) и словами главное — сколько требуют внимания. Сверху поиск
 * и переключатель дивизионов, первой строкой — недавние объекты.
 *
 * Используется дважды: страницей, пока объект не выбран, и в панели
 * снизу, чтобы перейти к другому объекту (тогда есть onClose).
 */
import { useMemo, useState } from 'react';

import { plural } from '../../domain/plural';
import Icon from './Icon';
import styles from './ObjectPicker.module.css';

const compact = (s) => s.toLowerCase().replace(/\s+/g, '');

/** Состояние объекта словами — цвет полоски его только дублирует. */
function statusOf(o) {
  if (o.open > 0) {
    return {
      tone: styles.toneOpen,
      text: `${o.open} ${plural(o.open, 'требует', 'требуют', 'требуют')} внимания`,
    };
  }
  if (o.checked > 0) return { tone: styles.toneChecked, text: 'замечания обработаны' };
  return { tone: styles.toneClean, text: 'без замечаний' };
}

function Tile({ o, selected, onSelect, small = false }) {
  const status = statusOf(o);
  const switches = `${o.total} ${plural(o.total, 'коммутатор', 'коммутатора', 'коммутаторов')}`;
  return (
    <button
      type="button"
      className={[styles.tile, small ? styles.tileSmall : '', selected ? styles.tileSelected : '']
        .filter(Boolean)
        .join(' ')}
      onClick={() => onSelect(o.mag)}
      aria-current={selected ? 'page' : undefined}
      aria-label={`${o.label}: ${switches}, ${status.text}${selected ? ', открыт сейчас' : ''}`}
    >
      <span className={styles.tileName}>{o.label}</span>
      {!small && <span className={styles.tileMeta}>{switches}</span>}
      <span className={styles.bar} aria-hidden="true">
        {o.clean > 0 && <span className={styles.segClean} style={{ flexGrow: o.clean }} />}
        {o.checked > 0 && <span className={styles.segChecked} style={{ flexGrow: o.checked }} />}
        {o.open > 0 && <span className={styles.segOpen} style={{ flexGrow: o.open }} />}
      </span>
      <span className={`${styles.tileStatus} ${status.tone}`}>{status.text}</span>
    </button>
  );
}

export default function ObjectPicker({
  divisions,
  recent = [],
  selectedMag,
  title,
  hint,
  onSelect,
  onClose,
}) {
  const [query, setQuery] = useState('');
  const [division, setDivision] = useState('');

  const search = compact(query.trim());
  const total = divisions.reduce((n, d) => n + d.objects.length, 0);

  const shown = useMemo(
    () =>
      divisions
        .filter((d) => !division || d.name === division)
        .map((d) => ({
          ...d,
          objects: d.objects.filter(
            (o) => !search || compact(o.label).includes(search) || o.mag.includes(search)
          ),
        }))
        .filter((d) => d.objects.length > 0),
    [divisions, division, search]
  );

  const recentObjects = useMemo(() => {
    if (search || division) return [];
    const byMag = new Map(divisions.flatMap((d) => d.objects.map((o) => [o.mag, o])));
    return recent.map((m) => byMag.get(m)).filter(Boolean);
  }, [divisions, recent, search, division]);

  const found = shown.reduce((n, d) => n + d.objects.length, 0);

  return (
    <div className={styles.picker}>
      <div className={styles.controls}>
        <div className={styles.headRow}>
          <div>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.hint}>
              {hint || `${total} ${plural(total, 'объект', 'объекта', 'объектов')}`}
            </p>
          </div>
          {onClose && (
            <button type="button" className={styles.close} onClick={onClose} aria-label="Закрыть">
              <Icon name="close" size={18} />
            </button>
          )}
        </div>

        <label className={styles.search}>
          <Icon name="search" size={18} className={styles.searchIcon} />
          <input
            type="search"
            placeholder="Номер объекта"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Найти объект"
            enterKeyHint="search"
          />
        </label>

        {divisions.length > 1 && (
          <div className={styles.chips} role="group" aria-label="Дивизион">
            <button
              type="button"
              className={`${styles.chip} ${division === '' ? styles.chipOn : ''}`}
              aria-pressed={division === ''}
              onClick={() => setDivision('')}
            >
              Все
            </button>
            {divisions.map((d) => (
              <button
                key={d.name}
                type="button"
                className={`${styles.chip} ${division === d.name ? styles.chipOn : ''}`}
                aria-pressed={division === d.name}
                onClick={() => setDivision(division === d.name ? '' : d.name)}
              >
                {d.name}
                <span className={styles.chipCount}>{d.objects.length}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={styles.body}>
        {recentObjects.length > 0 && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Недавние</h3>
            <div className={styles.recent}>
              {recentObjects.map((o) => (
                <Tile
                  key={o.mag}
                  o={o}
                  small
                  selected={o.mag === selectedMag}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </section>
        )}

        {found === 0 && (
          <div className={styles.none}>
            <div className={styles.noneTitle}>Объект не найден</div>
            <p>Проверьте номер: ищется по цифрам, например «134».</p>
          </div>
        )}

        {shown.map((d) => (
          <section key={d.name} className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {d.name}
              <span className={styles.sectionCount}>{d.objects.length}</span>
            </h3>
            <div className={styles.grid}>
              {d.objects.map((o) => (
                <Tile key={o.mag} o={o} selected={o.mag === selectedMag} onSelect={onSelect} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
